import { useEffect, useRef, useState } from 'react';

import { Button } from './Button';

export interface ModalAction {
  label: string;
  variant: 'secondary' | 'danger';
  onClick: () => void;
}

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  actions: ModalAction[];
  className?: string;
}

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
const ANIMATION_DURATION = 150;

export function Modal({ isOpen, onClose, title, children, actions, className = '' }: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const prevOpenRef = useRef(isOpen);
  const [mounted, setMounted] = useState(isOpen);
  const [visible, setVisible] = useState(isOpen);

  // Mount/unmount with animation: keep DOM alive during close transition
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => {
        setVisible(true);
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => {
        setMounted(false);
      }, ANIMATION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Focus first interactive element on open, restore on close
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      triggerRef.current = document.activeElement as HTMLElement;
      requestAnimationFrame(() => {
        if (modalRef.current) {
          const focusable = modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
          focusable[0]?.focus();
        }
      });
    }
    if (!isOpen && prevOpenRef.current) {
      triggerRef.current?.focus();
    }
    prevOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !modalRef.current) return;
    const el = modalRef.current;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const focusable = el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!mounted) return null;

  const opacityClass = visible ? 'opacity-100' : 'opacity-0';
  const scaleClass = visible ? 'scale-100' : 'scale-95';
  const easingClass = visible ? 'ease-out' : 'ease-in';

  return (
    <div className={'fixed inset-0 z-50 flex items-center justify-center ' + className}>
      <div data-testid="modal-scrim" className={'absolute inset-0 bg-[var(--sd-color-background)]/40 backdrop-blur-sm cursor-pointer transition-opacity duration-150 motion-reduce:transition-none ' + easingClass + ' ' + opacityClass} onClick={onClose} />
      <div ref={modalRef} data-testid="modal-panel" role="dialog" aria-modal="true" aria-labelledby="modal-title" className={'relative z-10 flex flex-col bg-sd-surface-elevated rounded-[var(--sd-radius-lg)] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] w-full max-w-md mx-sd-3 max-h-[80vh] transition-[opacity,transform] duration-150 motion-reduce:transition-none ' + easingClass + ' ' + opacityClass + ' ' + scaleClass}>
        <div className="flex-shrink-0 p-sd-4 pb-sd-2">
          <h2 id="modal-title" className="text-sd-lg font-bold text-sd-text m-0 tracking-tight">{title}</h2>
        </div>
        <div className="flex-1 overflow-y-auto px-sd-4 py-sd-2 text-sd-base text-sd-text-secondary leading-relaxed">{children}</div>
        <div className="flex-shrink-0 flex justify-end gap-sd-3 p-sd-4 pt-sd-2">
          {actions.map((act) => (
            <Button key={act.label} variant={act.variant} onClick={act.onClick}>
              {act.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
